import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Shared export helpers for admin tables.
 * Used by SalesManagement, BuyersList, FinancialSummary, DataModal, etc.
 */

export const exportToExcel = (data, filename) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Dados");
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export multiple datasets to a single Excel workbook, one sheet per dataset.
 * @param {Array<{name: string, data: object[]}>} sheets — sheet name + rows
 * @param {string} filename — without extension
 */
export const exportToExcelMulti = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) => {
    if (!s.data || s.data.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(s.data);
    XLSX.utils.book_append_sheet(wb, ws, (s.name || "Dados").slice(0, 31));
  });
  if (wb.SheetNames.length === 0) {
    // Fallback: at least one empty sheet so the file isn't invalid
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Sem dados"]]), "Vazio");
  }
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportToPDF = (data, columns, title, filename) => {
  // Auto landscape when many columns to avoid vertical-stacked headers
  const isWide = columns.length > 6;
  const doc = new jsPDF({
    orientation: isWide ? "landscape" : "portrait",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text(title, 14, 16);
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`Exportado em: ${new Date().toLocaleString("pt-PT")}`, 14, 22);

  autoTable(doc, {
    startY: 28,
    head: [columns.map((c) => c.header)],
    body: data.map((row) => columns.map((c) => row[c.key] ?? "")),
    theme: "grid",
    styles: {
      fontSize: 7.5,
      cellPadding: 1.5,
      overflow: "linebreak",
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [82, 82, 91],     // zinc-600 (cinza escuro)
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "left",
    },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: 8, right: 8 },
  });

  doc.save(`${filename}.pdf`);
};

/**
 * Render multiple tables in one PDF, stacked vertically.
 * @param {Array<{title: string, columns: {key,header}[], data: object[]}>} sections
 */
export const exportToPDFMulti = (sections, title, filename) => {
  const isWide = sections.some((s) => s.columns.length > 6);
  const doc = new jsPDF({
    orientation: isWide ? "landscape" : "portrait",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text(title, 14, 16);
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`Exportado em: ${new Date().toLocaleString("pt-PT")}`, 14, 22);

  let cursorY = 30;
  sections.forEach((sec, idx) => {
    if (!sec.data || sec.data.length === 0) return;
    if (idx > 0 || sections.length > 1) {
      doc.setFontSize(12);
      doc.setTextColor(40, 40, 40);
      doc.text(sec.title || "", 14, cursorY);
      cursorY += 4;
    }
    autoTable(doc, {
      startY: cursorY,
      head: [sec.columns.map((c) => c.header)],
      body: sec.data.map((row) => sec.columns.map((c) => row[c.key] ?? "")),
      theme: "grid",
      styles: { fontSize: 7.5, cellPadding: 1.5, overflow: "linebreak", textColor: [40, 40, 40], lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: [82, 82, 91], textColor: [255, 255, 255], fontStyle: "bold", halign: "left" },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      margin: { left: 8, right: 8 },
    });
    cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;
  });

  doc.save(`${filename}.pdf`);
};
